import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus, ShoppingBag, ChevronRight, Search, X } from "lucide-react";
import { api } from "../lib/api";
import { storeName } from "../lib/labels";
import SubCategoryTrend from "./SubCategoryTrend";
import ExportButtons from "./ExportButtons";
import { downloadCSV, downloadPDF, fmt } from "../lib/exports";
import { useSort } from "../lib/useSort";
import { SortHeader, SortChips } from "./SortControls";
import PeriodBadge from "./PeriodBadge";

function money(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function Delta({ pct, small = false }) {
  const size = small ? 10 : 12;
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-0.5 font-mono-bm text-[11px] text-neutral-400">
        <Minus size={size} strokeWidth={2} /> new
      </span>
    );
  }
  const up = pct >= 0;
  const cls = up ? "text-emerald-700" : "text-[#c72c2c]";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono-bm text-[11px] font-semibold ${cls}`}>
      <Icon size={size} strokeWidth={2.5} />
      {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

export default function AccessoriesPanel({ range, outletIds, compareMode = "same_period" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCats, setOpenCats] = useState({});
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!range) return;
    let cancel = false;
    setLoading(true);
    const params = { start_date: range.start, end_date: range.end };
    if (outletIds?.length) params.outlet_ids = outletIds.join(",");
    api.get("/products/accessories", { params })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range?.start, range?.end, outletIds?.join(",")]);

  const toggle = (cat) => setOpenCats((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const h = data?.headline;
  const byOutlet = data?.by_outlet || [];
  const allCategories = data?.categories || [];

  // Two independent sort states — one for the outer category rollup, one for
  // the nested per-SKU tables inside each expanded row. Shared across all
  // expanded panels so the ordering feels consistent as Roy scans.
  const catSort = useSort("revenue", "desc", {
    units: (r) => r.units,
    growth: (r) => r.unit_growth_pct,
    revenue: (r) => r.revenue,
  });
  const skuSort = useSort("revenue", "desc", {
    units: (r) => r.units,
    growth: (r) => r.unit_growth_pct,
    avg_price: (r) => r.avg_price,
    revenue: (r) => r.revenue,
  });

  // Filter categories AND SKUs by the search box. Category matches if its own
  // name matches OR any of its SKUs match. When searching we auto-expand any
  // hit so the user can see the matching SKUs immediately.
  const { categories, matchedSkuCount, autoExpand } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const src = catSort.apply(allCategories);
    if (!q) return { categories: src, matchedSkuCount: 0, autoExpand: {} };
    const out = [];
    const auto = {};
    let hits = 0;
    for (const c of src) {
      const catMatch = c.category.toLowerCase().includes(q);
      const skuMatches = (c.skus || []).filter((s) => {
        const hay = `${s.name || ""} ${s.parent_name || ""} ${s.sku || ""}`.toLowerCase();
        return hay.includes(q);
      });
      if (catMatch || skuMatches.length) {
        // When cat name matches, keep all SKUs; otherwise narrow to hits.
        const skus = catMatch ? c.skus : skuMatches;
        out.push({ ...c, skus, matched_skus: skuMatches.length });
        auto[c.category] = true;
        hits += skuMatches.length || (catMatch ? c.sku_count : 0);
      }
    }
    return { categories: out, matchedSkuCount: hits, autoExpand: auto };
  }, [allCategories, query, catSort]);

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="accessories-panel">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow inline-flex items-center gap-1">
          <ShoppingBag size={11} /> Table 5 · Accessories pulse
        </div>
        <div className="bm-eyebrow text-neutral-500">{range?.label || ""}</div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Accessories.</h3>
      <PeriodBadge range={range} className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-4">
        Jewelry, pins, belts, hats, sunglasses & novelty stock — click any category to see its SKUs
      </p>

      {loading && !data && <div className="text-sm text-neutral-500">Loading…</div>}

      {h && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="acc-headline-revenue">
            <div className="bm-eyebrow text-neutral-500">Revenue</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{money(h.revenue)}</div>
            <div className="mt-1"><Delta pct={h.revenue_delta_pct} /></div>
          </div>
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="acc-headline-units">
            <div className="bm-eyebrow text-neutral-500">Units</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{(h.units || 0).toLocaleString()}</div>
            <div className="mt-1"><Delta pct={h.units_delta_pct} /></div>
          </div>
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="acc-headline-txns">
            <div className="bm-eyebrow text-neutral-500">Transactions</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{(h.transactions || 0).toLocaleString()}</div>
            <div className="bm-eyebrow text-neutral-500 mt-1">baskets with acc.</div>
          </div>
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="acc-headline-aov">
            <div className="bm-eyebrow text-neutral-500">Avg. ticket</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{money(h.aov)}</div>
            <div className="bm-eyebrow text-neutral-500 mt-1">per basket</div>
          </div>
        </div>
      )}

      {byOutlet.length > 0 && (
        <div className="mb-6">
          <div className="bm-eyebrow text-neutral-500 mb-2">By store</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="acc-by-outlet">
            {byOutlet.map((o) => (
              <div key={o.outlet_id} className="border border-[#0a0a0a]/40 bg-[#f5f2eb] p-3">
                <div className="font-medium text-sm">{storeName(o.name)}</div>
                <div className="font-mono-bm tabular-nums text-lg mt-1">{money(o.revenue)}</div>
                <div className="bm-eyebrow text-neutral-500 font-mono-bm">
                  {(o.units || 0).toLocaleString()}u · {o.transactions}tx
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {categories.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="bm-eyebrow text-neutral-500">
              Categories · click to expand · {allCategories.length} groups
              {query && (
                <span className="ml-2 text-neutral-800">
                  · {categories.length} match{categories.length === 1 ? "" : "es"}
                  {matchedSkuCount ? ` (${matchedSkuCount} SKUs)` : ""}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <SortChips
                state={catSort}
                options={[
                  { k: "units", label: "Units" },
                  { k: "growth", label: "Δ" },
                  { k: "revenue", label: "Revenue" },
                ]}
                testIdPrefix="acc-cat-sort"
              />
              <ExportButtons
                disabled={!categories.length}
                testIdPrefix="accessories-export"
                onCSV={() => {
                  const columns = [
                    { key: "category", label: "Category" },
                    { key: "type", label: "Row type" },
                    { key: "name", label: "SKU description" },
                    { key: "sku", label: "SKU" },
                    { key: "units", label: "Units", align: "right" },
                    { key: "growth", label: "Δ vs prior (%)", align: "right" },
                    { key: "avg_price", label: "Avg $", align: "right" },
                    { key: "revenue", label: "Revenue", align: "right" },
                  ];
                  const flat = [];
                  for (const c of categories) {
                    flat.push({
                      category: c.category, type: "category",
                      name: `[${c.sku_count} SKUs]`, sku: "",
                      units: fmt.int(c.units),
                      growth: c.unit_growth_pct != null ? fmt.pct1(c.unit_growth_pct) : "",
                      avg_price: "", revenue: fmt.money0(c.revenue),
                    });
                    for (const s of (c.skus || [])) {
                      flat.push({
                        category: c.category, type: "sku",
                        name: s.parent_name ? `${s.parent_name} · ${s.name}` : s.name,
                        sku: s.sku || "",
                        units: fmt.int(s.units),
                        growth: s.unit_growth_pct != null ? fmt.pct1(s.unit_growth_pct) : "",
                        avg_price: `$${s.avg_price}`,
                        revenue: fmt.money0(s.revenue),
                      });
                    }
                  }
                  downloadCSV(`accessories-${range?.label || "range"}`, columns, flat);
                }}
                onPDF={() => {
                  const columns = [
                    { key: "n", label: "#", align: "right" },
                    { key: "category", label: "Category" },
                    { key: "sku_count", label: "SKUs", align: "right" },
                    { key: "units", label: "Units", align: "right" },
                    { key: "growth", label: "Δ vs prior", align: "right" },
                    { key: "revenue", label: "Revenue", align: "right" },
                  ];
                  const flat = categories.map((c, i) => ({
                    n: i + 1,
                    category: c.category,
                    sku_count: c.sku_count,
                    units: fmt.int(c.units),
                    growth: c.unit_growth_pct != null ? fmt.pct1(c.unit_growth_pct) : "",
                    revenue: fmt.money0(c.revenue),
                  }));
                  downloadPDF(
                    `accessories-${range?.label || "range"}`,
                    { title: "Accessories · category rollup", subtitle: `${range?.label || ""} · ${allCategories.length} groups` },
                    columns,
                    flat,
                  );
                }}
              />
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder='Filter categories or SKUs…'
                  className="pl-7 pr-7 py-1.5 bm-eyebrow border border-[#0a0a0a] bg-[#f5f2eb] w-56 focus:outline-none focus:ring-1 focus:ring-[#0a0a0a]"
                  data-testid="acc-search"
                />
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#0a0a0a] p-0.5"
                    aria-label="Clear search"
                    data-testid="acc-search-clear"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="border border-[#0a0a0a]/20" data-testid="acc-categories">
            {categories.map((c, i) => {
              const open = !!(openCats[c.category] || autoExpand[c.category]);
              return (
                <div key={c.category} className="border-b border-[#0a0a0a]/15 last:border-b-0">
                  <button
                    onClick={() => toggle(c.category)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#0a0a0a]/5 ${open ? "bg-[#0a0a0a]/5" : ""}`}
                    data-testid={`acc-cat-toggle-${i}`}
                    aria-expanded={open}
                  >
                    <ChevronRight
                      size={14}
                      className={`shrink-0 text-neutral-500 transition-transform ${open ? "rotate-90" : ""}`}
                    />
                    {/* Phone: name + $ + Δ only */}
                    <span className="md:hidden flex-1 min-w-0 flex items-center justify-between gap-3">
                      <span className="font-medium truncate">{c.category}</span>
                      <span className="flex items-center gap-3 shrink-0">
                        <Delta pct={c.unit_growth_pct} />
                        <span className="font-mono-bm tabular-nums font-semibold">{money(c.revenue)}</span>
                      </span>
                    </span>
                    <span className="hidden md:contents">
                    <span className="bm-eyebrow font-mono-bm text-neutral-500 w-8">#{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-medium flex-1 truncate">{c.category}</span>
                    <span className="bm-eyebrow px-1.5 py-0.5 border border-[#0a0a0a]/30 text-[9px]">
                      {c.sku_count} SKU{c.sku_count === 1 ? "" : "s"}
                    </span>
                    <span className="font-mono-bm tabular-nums w-20 text-right text-sm">{c.units.toLocaleString()}u</span>
                    <span className="w-20 text-right"><Delta pct={c.unit_growth_pct} /></span>
                    <span className="font-mono-bm tabular-nums w-24 text-right font-semibold">{money(c.revenue)}</span>
                    </span>
                  </button>

                  {open && (
                    <div className="bg-[#f5f2eb] border-t border-[#0a0a0a]/15" data-testid={`acc-cat-body-${i}`}>
                      <SubCategoryTrend
                        category={c.category}
                        range={range}
                        outletIds={outletIds}
                        compareMode={compareMode}
                      />
                      <div className="overflow-x-auto">
                        <table className="w-full text-[13px]">
                          <thead>
                            <tr className="border-b border-[#0a0a0a]/15">
                              <th className="text-left bm-eyebrow text-neutral-500 py-1.5 pl-3 md:pl-10 pr-2">SKU-level description</th>
                              <th className="text-left bm-eyebrow text-neutral-500 py-1.5 px-2 w-24 hidden md:table-cell">SKU</th>
                              <th className="text-right py-1.5 px-2 w-16 hidden md:table-cell"><SortHeader label="Units" k="units" state={skuSort} testId="acc-sku-sort-units" /></th>
                              <th className="text-right py-1.5 px-2 w-20"><SortHeader label="Δ vs prior" k="growth" state={skuSort} testId="acc-sku-sort-growth" /></th>
                              <th className="text-right py-1.5 px-2 w-16 hidden md:table-cell"><SortHeader label="Avg $" k="avg_price" state={skuSort} testId="acc-sku-sort-avg_price" /></th>
                              <th className="text-right py-1.5 pl-2 pr-3 w-24"><SortHeader label="Revenue" k="revenue" state={skuSort} testId="acc-sku-sort-revenue" /></th>
                            </tr>
                          </thead>
                          <tbody>
                            {skuSort.apply(c.skus).slice(0, 40).map((s, j) => (
                              <tr key={s.product_id} className="border-b border-[#0a0a0a]/10 last:border-b-0" data-testid={`acc-cat-${i}-sku-${j}`}>
                                <td className="py-1.5 pl-3 md:pl-10 pr-2 truncate max-w-[180px] md:max-w-[420px]" title={s.parent_name ? `${s.parent_name} · ${s.name}` : s.name}>
                                  {s.name}
                                </td>
                                <td className="py-1.5 px-2 font-mono-bm text-xs text-neutral-500 hidden md:table-cell">{s.sku || "—"}</td>
                                <td className={`py-1.5 px-2 text-right font-mono-bm tabular-nums hidden md:table-cell ${skuSort.sortKey === "units" ? "font-bold" : ""}`}>{s.units.toLocaleString()}</td>
                                <td className="py-1.5 px-2 text-right"><Delta pct={s.unit_growth_pct} small /></td>
                                <td className={`py-1.5 px-2 text-right font-mono-bm tabular-nums text-neutral-600 hidden md:table-cell ${skuSort.sortKey === "avg_price" ? "font-bold text-[#0a0a0a]" : ""}`}>${s.avg_price}</td>
                                <td className={`py-1.5 pl-2 pr-3 text-right font-mono-bm tabular-nums ${skuSort.sortKey === "revenue" ? "font-bold" : ""}`}>{money(s.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {c.skus.length > 40 && (
                          <div className="bm-eyebrow text-neutral-500 py-2 pl-10">
                            + {c.skus.length - 40} more SKUs (top 40 shown)
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {allCategories.length > 0 && categories.length === 0 && query && (
        <div className="border border-[#0a0a0a]/20 p-6 text-center bm-eyebrow text-neutral-500" data-testid="acc-search-empty">
          No categories or SKUs match "<span className="text-neutral-800">{query}</span>". Try "hat", "sunglass", or "belt".
        </div>
      )}

      {data?.matched_products != null && (
        <div className="bm-eyebrow text-neutral-500 mt-3">
          Scoped to {data.matched_products.toLocaleString()} SKUs tagged as accessories (categories containing "acc", hats, sunglasses, lighters, etc.)
        </div>
      )}
    </div>
  );
}
